import type { FieldValidationError } from "@/shared/types/workflow/workflow-form.types";
import { isCommentFieldValue } from "@/shared/utils/field-value-type-guards";
import { isRichTextValue } from "@/shared/utils/rich-text";
import type { Field, RichText } from "@spz/shared-domain";
import { RichTextUtils } from "@spz/shared-domain";
import type { TFunction } from "i18next";

/**
 * Validates if a required field is filled
 */
export function validateRequired({
  field,
  value,
  t,
}: {
  field: Field;
  value: unknown;
  t: TFunction;
}): FieldValidationError | undefined {
  const REQUIRED_ERROR: FieldValidationError = {
    translationKey: "required_field",
    type: "required",
    message: t("required_field"),
  };

  const INVALID_VALUE_ERROR: FieldValidationError = {
    translationKey: "workflow_validation_invalid_value",
    type: "validate",
    message: t("workflow_validation_invalid_value"),
  };

  if (!field.required) {
    return undefined;
  }

  switch (field.type) {
    case "textarea": {
      if (!isRichTextValue(value)) {
        return INVALID_VALUE_ERROR;
      }

      const textContent = RichTextUtils.getTextContent(value as RichText);
      if (textContent.trim().length > 0) {
        return undefined;
      }

      return REQUIRED_ERROR;
    }

    case "comment": {
      if (!isCommentFieldValue(value)) {
        return INVALID_VALUE_ERROR;
      }

      const contentText = value.content
        ? RichTextUtils.getTextContent(value.content as RichText)
        : "";

      // For comment fields, we consider the field filled if there's content
      // Attachment requirement is handled separately in comment validator
      if (contentText.trim().length > 0) {
        return undefined;
      }

      return REQUIRED_ERROR;
    }

    case "select": {
      const selectValue = value as
        | string
        | number
        | string[]
        | number[]
        | undefined;
      const isEmpty =
        !selectValue ||
        (Array.isArray(selectValue) && selectValue.length === 0) ||
        selectValue === "";

      if (isEmpty) {
        return REQUIRED_ERROR;
      }

      return undefined;
    }

    case "conclusion_select": {
      if (!value || value === "") {
        return REQUIRED_ERROR;
      }

      return undefined;
    }

    default: {
      if (!value) {
        return REQUIRED_ERROR;
      }

      return undefined;
    }
  }
}
